import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { Home, BarChart3, FileText, Menu, X, AlertCircle, Settings, Users, Activity, Terminal, AlertTriangle, UserPlus, ChevronDown, Trophy, Shield } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const NavContainer = styled.nav`
  background-color: ${({ theme }) => theme.colors.surface};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding: 0 ${({ theme }) => theme.spacing[4]};
  
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: 0 ${({ theme }) => theme.spacing[6]};
  }
`;

const NavContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
`;

const MobileMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background-color: ${({ theme }) => theme.colors.gray[100]};
    color: ${({ theme }) => theme.colors.primary[600]};
  }

  svg {
    width: 20px;
    height: 20px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const NavList = styled.ul<{ isOpen: boolean }>`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: ${({ theme }) => theme.spacing[1]};
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    flex-direction: column;
    background-color: ${({ theme }) => theme.colors.surface};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-top: none;
    border-radius: 0 0 ${({ theme }) => theme.borderRadius.lg} ${({ theme }) => theme.borderRadius.lg};
    box-shadow: ${({ theme }) => theme.shadows.lg};
    padding: ${({ theme }) => theme.spacing[4]};
    gap: ${({ theme }) => theme.spacing[2]};
    z-index: ${({ theme }) => theme.zIndex.dropdown};
    
    ${({ isOpen }) => !isOpen && `
      display: none;
    `}
  }
`;

const NavItem = styled.li`
  margin: 0;
  position: relative;
`;

const DropdownContainer = styled.div`
  position: relative;
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.text.secondary};
  background: none;
  border: none;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: all ${({ theme }) => theme.transitions.fast};
  cursor: pointer;
  white-space: nowrap;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};

  &:hover {
    color: ${({ theme }) => theme.colors.primary[600]};
    background-color: ${({ theme }) => theme.colors.primary[50]};
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
    width: 100%;
    justify-content: flex-start;
  }
`;

const DropdownMenu = styled.ul<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 200px;
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: ${({ theme }) => theme.spacing[2]};
  margin: 0;
  list-style: none;
  z-index: ${({ theme }) => theme.zIndex.dropdown};
  
  ${({ isOpen }) => !isOpen && `
    display: none;
  `}

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    position: static;
    box-shadow: none;
    border: none;
    padding: 0;
    margin-left: ${({ theme }) => theme.spacing[4]};
    background-color: transparent;
  }
`;

const StyledNavLink = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: all ${({ theme }) => theme.transitions.fast};
  white-space: nowrap;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};

  &:hover {
    color: ${({ theme }) => theme.colors.primary[600]};
    text-decoration: none;
    background-color: ${({ theme }) => theme.colors.primary[50]};
  }

  &.active {
    color: ${({ theme }) => theme.colors.primary[600]};
    background-color: ${({ theme }) => theme.colors.primary[100]};
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
    width: 100%;
    justify-content: flex-start;
  }
`;

const NavBrand = styled.div`
  display: flex;
  align-items: center;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const DesktopNav = styled.div`
  display: none;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
    width: 100%;
  }
`;

const MobileNav = styled.div`
  display: block;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

// Organized navigation items into logical groups
const mainNavItems = [
  {
    to: '/',
    label: 'Dashboard',
    icon: Home,
  },
  {
    to: '/results',
    label: 'Results',
    icon: BarChart3,
  },
  {
    to: '/competitions',
    label: 'Competitions',
    icon: Trophy,
  },
  {
    to: '/processing',
    label: 'Processing',
    icon: FileText,
  },
];

const evaluationItems = [
  {
    to: '/evaluation/questions',
    label: 'Questions',
    icon: Settings,
  },
  {
    to: '/evaluation/assignments',
    label: 'Assignments',
    icon: Users,
  },
  {
    to: '/evaluation/status',
    label: 'Status',
    icon: Activity,
  },
];

const managementItems = [
  {
    to: '/candidates',
    label: 'All Candidates',
    icon: Users,
  },
  {
    to: '/unmatched-drawings',
    label: 'Unmatched Drawings',
    icon: AlertCircle,
  },
  {
    to: '/candidate-conflicts',
    label: 'Conflicts',
    icon: AlertTriangle,
  },
];

const systemItems = [
  {
    to: '/logs',
    label: 'Live Logs',
    icon: Terminal,
  },
];

interface DropdownProps {
  label: string;
  icon: React.ComponentType<any>;
  items: Array<{ to: string; label: string; icon: React.ComponentType<any> }>;
  isMobile?: boolean;
  onItemClick?: () => void;
}

const Dropdown: React.FC<DropdownProps> = ({ label, icon: Icon, items, isMobile = false, onItemClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleItemClick = () => {
    setIsOpen(false);
    onItemClick?.();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownButton onClick={toggleDropdown}>
        <Icon />
        {label}
        <ChevronDown style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
      </DropdownButton>
      <DropdownMenu isOpen={isOpen}>
        {items.map(({ to, label, icon: ItemIcon }) => (
          <NavItem key={to}>
            <StyledNavLink to={to} onClick={handleItemClick}>
              <ItemIcon />
              {label}
            </StyledNavLink>
          </NavItem>
        ))}
      </DropdownMenu>
    </DropdownContainer>
  );
};

export const Navigation: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isAdmin } = useAuth();
  
  console.log('[Navigation] Is admin:', isAdmin);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <NavContainer>
      <NavContent>
        <NavBrand>
          Drawing Evaluator
        </NavBrand>
        
        <MobileMenuButton onClick={toggleMobileMenu} aria-label="Toggle navigation menu">
          {isMobileMenuOpen ? <X /> : <Menu />}
        </MobileMenuButton>

        <DesktopNav>
          <NavList isOpen={true}>
            {/* Main navigation items */}
            {mainNavItems.map(({ to, label, icon: Icon }) => (
              <NavItem key={to}>
                <StyledNavLink to={to}>
                  <Icon />
                  {label}
                </StyledNavLink>
              </NavItem>
            ))}
            
            {/* Evaluation dropdown */}
            <NavItem>
              <Dropdown
                label="Evaluation"
                icon={Settings}
                items={evaluationItems}
              />
            </NavItem>
            
            {/* Management dropdown */}
            <NavItem>
              <Dropdown
                label="Management"
                icon={Users}
                items={managementItems}
              />
            </NavItem>
            
            {/* System items */}
            {systemItems.map(({ to, label, icon: Icon }) => (
              <NavItem key={to}>
                <StyledNavLink to={to}>
                  <Icon />
                  {label}
                </StyledNavLink>
              </NavItem>
            ))}
            
            {/* Admin only - User Management */}
            {isAdmin && (
              <NavItem>
                <StyledNavLink to="/users">
                  <Shield />
                  Users
                </StyledNavLink>
              </NavItem>
            )}
            
            {/* Settings - Available to all users */}
            <NavItem>
              <StyledNavLink to="/settings">
                <Settings />
                Settings
              </StyledNavLink>
            </NavItem>
          </NavList>
        </DesktopNav>

        {/* Mobile Navigation - Only visible on mobile */}
        <MobileNav>
          <NavList isOpen={isMobileMenuOpen}>
            {/* Main items */}
            {mainNavItems.map(({ to, label, icon: Icon }) => (
              <NavItem key={to}>
                <StyledNavLink to={to} onClick={closeMobileMenu}>
                  <Icon />
                  {label}
                </StyledNavLink>
              </NavItem>
            ))}
            
            {/* Evaluation section */}
            <NavItem>
              <Dropdown
                label="Evaluation"
                icon={Settings}
                items={evaluationItems}
                isMobile={true}
                onItemClick={closeMobileMenu}
              />
            </NavItem>
            
            {/* Management section */}
            <NavItem>
              <Dropdown
                label="Management"
                icon={Users}
                items={managementItems}
                isMobile={true}
                onItemClick={closeMobileMenu}
              />
            </NavItem>
            
            {/* System items */}
            {systemItems.map(({ to, label, icon: Icon }) => (
              <NavItem key={to}>
                <StyledNavLink to={to} onClick={closeMobileMenu}>
                  <Icon />
                  {label}
                </StyledNavLink>
              </NavItem>
            ))}
            
            {/* Admin only - User Management */}
            {isAdmin && (
              <NavItem>
                <StyledNavLink to="/users" onClick={closeMobileMenu}>
                  <Shield />
                  Users
                </StyledNavLink>
              </NavItem>
            )}
            
            {/* Settings - Available to all users */}
            <NavItem>
              <StyledNavLink to="/settings" onClick={closeMobileMenu}>
                <Settings />
                Settings
              </StyledNavLink>
            </NavItem>
          </NavList>
        </MobileNav>
      </NavContent>
    </NavContainer>
  );
};